import { Canvas as FabricCanvas, FabricObject } from 'fabric';

export interface ViewportBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Get the current viewport bounds considering zoom and pan transformations
 */
export function getViewportBounds(canvas: FabricCanvas): ViewportBounds {
  const vpt = canvas.viewportTransform;
  if (!vpt) {
    return {
      left: 0,
      top: 0,
      right: canvas.getWidth(),
      bottom: canvas.getHeight()
    };
  }

  // Calculate the viewport bounds in object space
  const zoom = canvas.getZoom();
  const panX = vpt[4];
  const panY = vpt[5];

  // Convert screen coordinates to object coordinates
  const left = -panX / zoom;
  const top = -panY / zoom;
  const right = (-panX + canvas.getWidth()) / zoom;
  const bottom = (-panY + canvas.getHeight()) / zoom;

  return { left, top, right, bottom };
}

/**
 * Check if an object is within the viewport
 */
export function isObjectInViewport(obj: FabricObject, viewportBounds: ViewportBounds): boolean {
  // Get object bounds
  const objBounds = obj.getBoundingRect();
  
  // Add a small margin to prevent objects from popping in/out at edges
  const margin = 50;
  
  // Check if object intersects with viewport
  return !(
    objBounds.left > viewportBounds.right + margin ||
    objBounds.left + objBounds.width < viewportBounds.left - margin ||
    objBounds.top > viewportBounds.bottom + margin ||
    objBounds.top + objBounds.height < viewportBounds.top - margin
  );
}

/**
 * Get objects that are visible in the current viewport
 */
export function getVisibleObjects(canvas: FabricCanvas): FabricObject[] {
  const viewportBounds = getViewportBounds(canvas);
  const allObjects = canvas.getObjects();
  
  return allObjects.filter(obj => {
    // Always render certain critical objects
    const name = obj.get?.('name') || (obj as any).name;
    if (name === 'questionBackground' || 
        name === 'parentQuestionBackground' ||
        name?.startsWith('questionOpticalChoice_') || 
        name?.startsWith('opticalChoice_')) {
      return true;
    }
    
    return isObjectInViewport(obj, viewportBounds);
  });
}

/**
 * Optimized render function that only renders visible objects
 */
export function renderVisibleObjects(canvas: FabricCanvas, ctx: CanvasRenderingContext2D): void {
  // Clear the canvas
  canvas.clearContext(ctx);
  
  // Render background
  (canvas as any)._renderBackground?.(ctx);
  
  // Get only visible objects
  const visibleObjects = getVisibleObjects(canvas);
  
  // Render visible objects
  if ((canvas as any)._renderObjects) {
    (canvas as any)._renderObjects(ctx, visibleObjects);
  }
  
  // Render overlay if exists
  (canvas as any)._renderOverlay?.(ctx);
}

/**
 * Set up viewport culling for a canvas
 * This version works by storing the culling functions globally for integration with existing render overrides
 */
export function setupViewportCulling(canvas: FabricCanvas): () => void {
  // Store viewport culling functions globally for access in render overrides
  if (!(window as any).viewportCulling) {
    (window as any).viewportCulling = {
      getViewportBounds,
      isObjectInViewport,
      getVisibleObjects
    };
  }
  
  // Track culling stats
  const lastCullStats = {
    frameCount: 0,
    totalCulled: 0,
    avgCullPercentage: 0
  };
  
  // Add pan/zoom event listeners to trigger re-renders when viewport changes
  const handleViewportChange = () => {
    canvas.requestRenderAll();
  };
  
  canvas.on('mouse:wheel', handleViewportChange);
  canvas.on('mouse:move', (_opt: any) => {
    if ((canvas as any).isDragging) {
      handleViewportChange();
    }
  });
  
  // Return cleanup function
  return () => {
    canvas.off('mouse:wheel', handleViewportChange);
    canvas.off('mouse:move', handleViewportChange);
  };
}

/**
 * Debug function to visualize viewport bounds
 */
export function debugViewportBounds(canvas: FabricCanvas): void {
  const viewportBounds = getViewportBounds(canvas);
  const visibleObjects = getVisibleObjects(canvas);
  

  
  // Log which objects are culled
  const culledObjects = canvas.getObjects().filter(obj => 
    !visibleObjects.includes(obj)
  );
  
  
}

/**
 * Performance monitoring for viewport culling
 */
export interface CullingStats {
  totalObjects: number;
  visibleObjects: number;
  culledObjects: number;
  renderTime: number;
}

export function monitorCullingPerformance(canvas: FabricCanvas): CullingStats {
  const startTime = performance.now();
  const viewportBounds = getViewportBounds(canvas);
  const allObjects = canvas.getObjects();
  const visibleObjects = allObjects.filter(obj => isObjectInViewport(obj, viewportBounds));
  const endTime = performance.now();
  
  return {
    totalObjects: allObjects.length,
    visibleObjects: visibleObjects.length,
    culledObjects: allObjects.length - visibleObjects.length,
    renderTime: endTime - startTime
  };
}